// LearnCore Frontend - 学习时间统计页面

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import Loading from '../components/Loading';
import type { StudyTimeStats, StudyTimeLog } from '../types';
import {
  ClockIcon,
  FireIcon,
  BookOpenIcon,
  ChartBarIcon,
  CalendarIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

export default function StudyTimePage() {
  const [stats, setStats] = useState<StudyTimeStats | null>(null);
  const [recentLogs, setRecentLogs] = useState<StudyTimeLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'all'>('week');

  useEffect(() => {
    loadStats();
  }, [period]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [statsData, logsData] = await Promise.all([
        apiClient.getStudyTimeStats(period),
        apiClient.getRecentStudyTimeLogs(10)
      ]);
      
      setStats(statsData);
      setRecentLogs(logsData.logs);
    } catch (err: any) {
      setError(err.response?.data?.error || '加载学习时间统计失败');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    // 边界检查，确保秒数是非负数
    if (seconds < 0) seconds = 0;
    if (seconds > 86400) seconds = 86400; // 超过24小时则限制为24小时

    if (seconds < 60) return `${seconds}秒`;

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return minutes > 0 ? `${hours}小时${minutes}分钟` : `${hours}小时`;
    }
    return `${minutes}分钟`;
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return '今天';
    if (diffDays === 1) return '昨天';
    if (diffDays < 7) return `${diffDays}天前`;
    
    return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
  };

  const getModeLabel = (mode: string): string => {
    switch (mode) {
      case 'flashcard': return '抽认卡';
      case 'definitionQuiz': return '定义测验';
      case 'termQuiz': return '术语测验';
      default: return mode;
    }
  };

  const getModeColor = (mode: string): string => {
    switch (mode) {
      case 'flashcard': return 'bg-blue-100 text-blue-800';
      case 'definitionQuiz': return 'bg-green-100 text-green-800';
      case 'termQuiz': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading size="lg" text="加载学习统计..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <ClockIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{error}</h1>
          <button onClick={loadStats} className="btn-primary">
            重试
          </button>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  // 添加边界检查和数据验证
  const totalActiveTime = Math.max(0, stats.total.total_active_time || 0);
  const totalTime = Math.max(0, stats.total.total_time || 0);

  // 确保活跃时间不超过总时间
  const adjustedActiveTime = Math.min(totalActiveTime, totalTime);
  const activeRate = totalTime > 0 ? Math.round((adjustedActiveTime / totalTime) * 100) : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          学习时间统计
        </h1>
        
        {/* 时间段选择 */}
        <div className="flex space-x-2">
          {(['day', 'week', 'month', 'all'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                period === p
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {p === 'day' && '今日'}
              {p === 'week' && '本周'}
              {p === 'month' && '本月'}
              {p === 'all' && '全部'}
            </button>
          ))}
        </div>
      </div>

      {/* 总体统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-primary-100 rounded-lg p-3">
              <ClockIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">有效学习时间</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatDuration(adjustedActiveTime)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-blue-100 rounded-lg p-3">
              <FireIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">学习效率</p>
              <p className="text-2xl font-bold text-gray-900">{activeRate}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-100 rounded-lg p-3">
              <BookOpenIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">学习卡片数</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.total.total_cards_studied || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-purple-100 rounded-lg p-3">
              <ChartBarIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">学习次数</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.total.session_count || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* 按模式统计 */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <ChartBarIcon className="h-5 w-5 mr-2 text-gray-500" />
              学习模式统计
            </h2>
          </div>
          <div className="p-6">
            {stats.by_mode.length === 0 ? (
              <p className="text-gray-500 text-center py-8">暂无学习记录</p>
            ) : (
              <div className="space-y-4">
                {stats.by_mode.map((modeData) => {
                  // 数据验证
                  const activeDuration = Math.max(0, modeData.active_duration || 0);
                  const cardsStudied = Math.max(0, modeData.cards_studied || 0);
                  const sessionCount = Math.max(0, modeData.session_count || 0);

                  // 确保活跃时间不超过总活跃时间
                  const safeActiveDuration = Math.min(activeDuration, adjustedActiveTime);
                  const percentage = adjustedActiveTime > 0 ? (safeActiveDuration / adjustedActiveTime) * 100 : 0;

                  return (
                    <div key={modeData.mode} className="border-b border-gray-100 pb-4 last:border-0">
                      <div className="flex justify-between items-center mb-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getModeColor(modeData.mode)}`}>
                          {getModeLabel(modeData.mode)}
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                          {formatDuration(safeActiveDuration)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full"
                          style={{
                            width: `${percentage}%`
                          }}
                        />
                      </div>
                      <div className="mt-2 text-xs text-gray-500 flex justify-between">
                        <span>{cardsStudied} 张卡片</span>
                        <span>{sessionCount} 次学习</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* 按学习集统计 */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <BookOpenIcon className="h-5 w-5 mr-2 text-gray-500" />
              学习集统计
            </h2>
          </div>
          <div className="p-6">
            {stats.by_set.length === 0 ? (
              <p className="text-gray-500 text-center py-8">暂无学习记录</p>
            ) : (
              <div className="space-y-4">
                {stats.by_set.slice(0, 5).map((setData) => {
                  // 数据验证
                  const activeDuration = Math.max(0, setData.active_duration || 0);
                  const cardsStudied = Math.max(0, setData.cards_studied || 0);
                  const sessionCount = Math.max(0, setData.session_count || 0);

                  // 确保活跃时间不超过总活跃时间
                  const safeActiveDuration = Math.min(activeDuration, adjustedActiveTime);
                  const percentage = adjustedActiveTime > 0 ? (safeActiveDuration / adjustedActiveTime) * 100 : 0;

                  return (
                    <div key={setData.set_id} className="border-b border-gray-100 pb-4 last:border-0">
                      <div className="flex justify-between items-start mb-2">
                        <Link
                          to={`/sets/${setData.set_id}/view`}
                          className="text-sm font-medium text-primary-600 hover:text-primary-700 flex-1 mr-4 truncate"
                        >
                          {setData.title}
                        </Link>
                        <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                          {formatDuration(safeActiveDuration)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-600 h-2 rounded-full"
                          style={{
                            width: `${percentage}%`
                          }}
                        />
                      </div>
                      <div className="mt-2 text-xs text-gray-500 flex justify-between">
                        <span>{cardsStudied} 张卡片</span>
                        <span>{sessionCount} 次学习</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 每日趋势 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <CalendarIcon className="h-5 w-5 mr-2 text-gray-500" />
            学习趋势
          </h2>
        </div>
        <div className="p-6">
          {stats.daily.length === 0 ? (
            <p className="text-gray-500 text-center py-8">暂无学习记录</p>
          ) : (
            <div className="space-y-3">
              {stats.daily.map((day) => {
                // 数据验证
                const activeDuration = Math.max(0, day.active_duration || 0);
                const cardsStudied = Math.max(0, day.cards_studied || 0);

                const maxTime = Math.max(...stats.daily.map(d => Math.max(0, d.active_duration || 0)));
                const percentage = maxTime > 0 ? (activeDuration / maxTime) * 100 : 0;

                return (
                  <div key={day.date} className="flex items-center">
                    <div className="w-24 text-sm text-gray-600">
                      {new Date(day.date).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })}
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="w-full bg-gray-200 rounded-full h-8 relative">
                        <div
                          className="bg-primary-600 h-8 rounded-full flex items-center justify-end pr-3"
                          style={{ width: `${Math.max(percentage, 5)}%` }}
                        >
                          <span className="text-xs font-medium text-white">
                            {formatDuration(activeDuration)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="w-20 text-right text-sm text-gray-500">
                      {cardsStudied} 卡片
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 最近学习记录 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <ArrowTrendingUpIcon className="h-5 w-5 mr-2 text-gray-500" />
            最近学习记录
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  学习集
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  模式
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  学习时长
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  卡片数
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  时间
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentLogs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    暂无学习记录
                  </td>
                </tr>
              ) : (
                recentLogs.map((log) => {
                  // 数据验证
                  const activeDuration = Math.max(0, log.active_duration || 0);
                  const cardsStudied = Math.max(0, log.cards_studied || 0);

                  return (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <Link
                          to={`/sets/${log.set_id}/view`}
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          {log.set_title || '未命名学习集'}
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getModeColor(log.mode)}`}>
                          {getModeLabel(log.mode)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatDuration(activeDuration)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {cardsStudied} 张
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(log.start_time)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
